import React, { useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { brandHeading } from '@/utils/brand-typography'
import { ProductTypes } from '@/modules/base/util/constants'
import SubscriptionService from '@/modules/base/components/subscription/subscriptionService'
import { Module, ModuleType } from '@/modules/moduleSubscription/types/module'
import { ShoppingCartItems } from '@/modules/moduleSubscription/types/ShoppingCart'
import { SubscriptionType } from '@/redux/ducks/subscription/types/details'
import AddonTile from './addon-tile'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 24,
    borderRadius: theme.borderRadiusExtraExtraLarge,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.brand.divider}`,
    boxShadow: theme.shadowCard,
  },
  title: {
    margin: 0,
    ...brandHeading(theme, 'h6'),
    color: theme.palette.brand.text.primary,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  empty: {
    color: theme.palette.brand.text.secondary,
  },
}))

interface AddonsStepProps {
  items: ShoppingCartItems
  period: string
  activeSubscriptions: SubscriptionType[]
  // already loaded with the plan selection - shown until the add-ons of the selected plan are loaded
  moduleData?: ModuleType | null
  onChange: (type: string, module: Module, quantity: number) => void
  onRemove: (type: string, moduleName: string) => void
}

const getModuleData = async(name: string): Promise<ModuleType | null> => {
  const response = await SubscriptionService.moduleSubscriptions.getModulesOfSubscriptionType(name)
  return response?.data || null
}

// the add-ons are booked for the main product - either the plan in the cart or the active one
const getMainSubscriptionName = (items: ShoppingCartItems, activeSubscriptions: SubscriptionType[]) => {
  const planInCart = Object.values(items[ProductTypes.MAIN] ?? {}).find(item => item.type)
  return planInCart?.name || activeSubscriptions.find(sub => sub.typeOfSubscription === ProductTypes.MAIN)?.type
}

// a singleton add-on that is already included in another product of the cart cannot be booked twice
const getDisabledBy = (module: Module, subscriptionType: string, items: ShoppingCartItems) => {
  if (!module.singleton) return null
  return Object.keys(items).find(type => {
    if (type === subscriptionType) return false
    return Object.values(items[type]).some(item => {
      const modules = (item as { modules?: { module: { name: string }, includedCount: number }[] }).modules
      return modules?.some(({ module: { name }, includedCount }) => name === module.name && includedCount)
    })
  }) || null
}

const AddonsStep = (props: AddonsStepProps) => {
  const { items, period, activeSubscriptions, moduleData: moduleDataProps, onChange, onRemove } = props
  const classes = useStyles()
  const subscriptionName = getMainSubscriptionName(items, activeSubscriptions)
  const [moduleData, setModuleData] = useState<ModuleType | null>(moduleDataProps || null)

  useEffect(() => {
    if (!subscriptionName) {
      setModuleData(null)
      return
    }

    let isCurrent = true
    getModuleData(subscriptionName).then((data) => {
      if (isCurrent) setModuleData(data)
    })
    return () => { isCurrent = false }
  }, [subscriptionName])

  const availableModules = moduleData?.availableModules || []
  const subscriptionType = moduleData?.type

  return (
    <section className={classes.root} data-qs-id='checkout-addons-step'>
      <h2 className={classes.title}>{lang.upgrade.wizard.addons.title}</h2>
      {availableModules.length
        ? (
          <div className={classes.grid}>
            {availableModules.map(module => {
              const disabledBy = getDisabledBy(module, subscriptionType, items)
              const disabled = Boolean(disabledBy) || Boolean(moduleData?.notAllowed)
              return (
                <AddonTile
                  key={module.name}
                  type={subscriptionType}
                  module={module}
                  count={items?.[subscriptionType]?.[module.name]?.count || 0}
                  period={period}
                  disabled={disabled}
                  disabledHint={disabled ? lang.upgradeModal.modules.disabledHint.default : undefined}
                  onChange={onChange}
                  onRemove={onRemove}
                />
              )
            })}
          </div>
        )
        : <div className={classes.empty}>{lang.upgradeModal.modules.emptySelection}</div>}
    </section>
  )
}

export default AddonsStep
